import React, { ReactNode, MouseEvent } from 'react';
import Button from '../Button';
import ModalCore from './core/ModalCore';
import { prefixCls } from './constants';
import './Modal.css';
import withTheme from '../../hocs/withTheme';
import classNames from 'classnames';
import { BUTTON_TYPES } from '../Button/constants';

// Define the shape of the component props
export interface ModalProps {
    footer?: ReactNode;
    visible?: boolean;
    wrapClassName?: string;
    width?: string | number;
    cancelButtonProps?: Record<string, any>;
    okButtonProps?: Record<string, any>;
    okText?: ReactNode;
    cancelText?: ReactNode;
    closeIcon?: ReactNode;
    closable?: boolean;
    borderRadius?: boolean;
    className?: string;
    buttonsStreched?: boolean;
    okType?: typeof BUTTON_TYPES[number];
    confirmLoading?: boolean;
    onCancel?: (e: MouseEvent<HTMLElement>) => void;
    onOk?: (e: MouseEvent<HTMLElement>) => void;
    onClose?: (e: MouseEvent<HTMLElement>) => void;
    children?: ReactNode;
    title?: ReactNode;
    // ... any other props passed to ModalCore via restProps
    [key: string]: any; 
}

const Modal: React.FC<ModalProps> = (props) => {
    const {
        footer = undefined,
        visible = false,
        wrapClassName = '',
        width = 380,
        cancelButtonProps = {},
        okButtonProps = {},
        okText = 'OK',
        cancelText = 'Cancel',
        closeIcon = null,
        closable = false,
        borderRadius = true,
        className,
        buttonsStreched = true,
        okType = 'primary',
        confirmLoading = false,
        onCancel,
        onOk,
        onClose,
        ...restProps
    } = props;

    const classes = classNames(className, {
        [`has-radius`]: borderRadius
    });

    const handleCancel = (e: MouseEvent<HTMLElement>) => {
        if (typeof onCancel === 'function') {
            onCancel(e);
        }
    };

    const handleClose = (e: MouseEvent<HTMLElement>) => {
        if (typeof onClose === 'function') {
            onClose(e);
        } else if (typeof onCancel === 'function') {
            onCancel(e);
        }
    };

    const handleOk = (e: MouseEvent<HTMLElement>) => {
        if (typeof onOk === 'function') {
            onOk(e);
        }
    };

    const renderFooter = (): JSX.Element => {
        return (
            <div className='modal-footer--button-group'>
                {cancelText && (
                    <Button onClick={handleCancel} {...cancelButtonProps}>
                        {cancelText}
                    </Button>
                )}
                {!okButtonProps?.hidden && <Button
                    type={okType}
                    loading={confirmLoading}
                    onClick={handleOk}
                    {...okButtonProps}
                >
                    {okText}
                </Button>}
            </div>
        );
    };

    return (
        <ModalCore
            {...restProps}
            width={width}
            prefixCls={prefixCls}
            footer={footer === undefined ? renderFooter() : footer}
            visible={visible}
            onClose={handleClose}
            transitionName="zoom"
            closable={closable}
            closeIcon={closeIcon}
            className={classes}
        />
    );
};

export default withTheme(Modal);